import { TMDB_IMAGE_BASE, IMAGE_SIZES } from './constants';

export function getPosterUrl(
  path: string | null,
  size: keyof typeof IMAGE_SIZES.poster = 'medium'
): string {
  if (!path) {
    return '/placeholder-poster.svg';
  }
  return `${TMDB_IMAGE_BASE}/${IMAGE_SIZES.poster[size]}${path}`;
}

export function getBackdropUrl(
  path: string | null,
  size: keyof typeof IMAGE_SIZES.backdrop = 'medium'
): string {
  if (!path) {
    return '';
  }
  return `${TMDB_IMAGE_BASE}/${IMAGE_SIZES.backdrop[size]}${path}`;
}

export function getProfileUrl(
  path: string | null,
  size: keyof typeof IMAGE_SIZES.profile = 'medium'
): string {
  if (!path) {
    return '/placeholder-profile.svg';
  }
  return `${TMDB_IMAGE_BASE}/${IMAGE_SIZES.profile[size]}${path}`;
}

export function getRatingColor(rating: number): string {
  if (rating >= 8) return 'bg-emerald-500';
  if (rating >= 7) return 'bg-lime-500';
  if (rating >= 6) return 'bg-yellow-500';
  if (rating >= 5) return 'bg-orange-500';
  return 'bg-red-500';
}

export function getRatingTextColor(rating: number): string {
  if (rating >= 8) return 'text-emerald-500';
  if (rating >= 7) return 'text-lime-500';
  if (rating >= 6) return 'text-yellow-500';
  if (rating >= 5) return 'text-orange-500';
  return 'text-red-500';
}

export function formatRuntime(minutes: number): string {
  if (!minutes) return 'N/A';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
}

export function formatYear(dateString: string): string {
  if (!dateString) return 'N/A';
  return dateString.split('-')[0];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout>;
  return function executedFunction(...args: Parameters<T>) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

export function hashParams(params: Record<string, unknown>): string {
  return btoa(JSON.stringify(params)).replace(/[+/=]/g, '');
}

/**
 * Calculate "Our Rating" - a weighted combination of IMDB and Rotten Tomatoes scores
 * IMDB: 60% weight (already 0-10 scale)
 * RT: 40% weight (converted from 0-100 to 0-10 scale)
 * Returns a score from 0-10, or null if no ratings available
 */
export function calculateOurRating(imdbRating?: number, rtRating?: number): number | null {
  if (!imdbRating && !rtRating) return null;

  // If only one rating is available, use it
  if (imdbRating && !rtRating) return imdbRating;
  if (!imdbRating && rtRating) return rtRating / 10;

  // Both ratings available - calculate weighted average
  const imdbWeight = 0.6;
  const rtWeight = 0.4;
  const rtNormalized = (rtRating as number) / 10; // Convert RT from 0-100 to 0-10

  return Math.round((imdbRating! * imdbWeight + rtNormalized * rtWeight) * 10) / 10;
}
